import EventSource, { type EventSourceListener } from 'react-native-sse'
import type {
  CallEndedEventTypeEnum,
  CallRequestedEventTypeEnum,
  ItemVonageCredentials,
  CallRequestAcceptedEventTypeEnum,
  CallRequestDeclinedEventTypeEnum,
} from '@dialogue/toktok'
import Config from 'APP/Config'
import { logger } from 'APP/Lib/Datadog'
import apisauce from 'apisauce'
import { isAccessTokenValid } from 'APP/Lib/AuthHelpers'
import axiosRetry from 'axios-retry'
import type { AxiosInstance } from 'axios'
import { AcceptCallRequest } from '@dialogue/toktok'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import { DeclineCallRequest } from '@dialogue/toktok/api'

type EventType =
  | CallEndedEventTypeEnum
  | CallRequestAcceptedEventTypeEnum
  | CallRequestDeclinedEventTypeEnum
  | CallRequestedEventTypeEnum
type CallEndedListener = EventSourceListener<EventType, CallEndedEventTypeEnum>
type CallRequestAcceptedListener = EventSourceListener<EventType, CallRequestAcceptedEventTypeEnum>
type CallRequestDeclinedListener = EventSourceListener<EventType, CallRequestDeclinedEventTypeEnum>
type CallRequestedListener = EventSourceListener<EventType, CallRequestedEventTypeEnum>

const createEventSource = (
  accessToken: string | null | undefined,
  lastEventId: string | null = null,
  baseURL: string | null | undefined = Config.TOKTOK_SERVICE_URL
) => {
  if (!baseURL) {
    logger.warn('[SSE] No base URL provided, cannot initialize Toktok EventSource object')
    return
  }

  if (!isAccessTokenValid(accessToken)) {
    logger.info('[SSE] Access token is not valid, cannot initialize Toktok EventSource object')
    return
  }

  const endpointUrl = new URL('v1/events', baseURL)

  const es = new EventSource<EventType>(endpointUrl, {
    headers: {
      Authorization: {
        toString: function () {
          return 'Bearer ' + accessToken
        },
      },
    },
    debug: __DEV__,
  })

  if (lastEventId) {
    // TODO: when this PR is merged, update react-native-sse library and set
    // lastEventId in the EventSource constructor options above.
    // This PR also includes proper typing, so @ts-expect-error can be removed
    // https://github.com/binaryminds/react-native-sse/pull/82
    // @ts-expect-error Property is defined but not exposed by the library's typings
    es.lastEventId = lastEventId
  }
  return es
}

const create = (
  accessToken: string | null,
  baseURL: string | null | undefined = Config.TOKTOK_SERVICE_URL
) => {
  if (!baseURL) {
    throw new Error('Unable to connect to Toktok. No base URL provided.')
  }

  if (!isAccessTokenValid(accessToken)) {
    throw new Error('Unable to connect to Toktok. Access token is not valid.')
  }

  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 20000,
  })

  axiosRetry(api.axiosInstance as AxiosInstance, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: axiosRetry.isNetworkOrIdempotentRequestError,
    onRetry: (retryCount, error, requestConfig) => {
      logger.info('[Toktok] Retrying request', {
        retryCount,
        url: requestConfig.url,
        method: requestConfig.method,
        errorStatus: error.response?.status,
        errorData: error.response?.data,
      })
    },
  })

  const getSessionCredentials = async (sessionId: string): Promise<ItemVonageCredentials> => {
    const response = await api.get<ItemVonageCredentials, unknown>(
      `/v1/sessions/${sessionId}/token`
    )
    if (response.ok && response.data) {
      return response.data
    }
    const errorMessage = 'Error getting session credentials from Toktok'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  const acceptCallRequest = async (sessionId: string): Promise<void> => {
    const body: AcceptCallRequest = {
      device_id: DeviceInfo.getUniqueId(),
    }

    const response = await api.post(`/v1/sessions/${sessionId}/accept_request`, body)
    if (response.ok) {
      return
    }
    const errorMessage = 'Error accepting call request from Toktok'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  const declineCallRequest = async (sessionId: string): Promise<void> => {
    const body: DeclineCallRequest = {
      device_id: DeviceInfo.getUniqueId(),
    }

    const response = await api.post(`/v1/sessions/${sessionId}/decline_request`, body)
    if (response.ok) {
      return
    }
    const errorMessage = 'Error declining call request from Toktok'
    logger.error(errorMessage, {
      ok: response?.ok,
      problem: response?.problem,
      data: response?.data,
      status: response?.status,
      duration: response?.duration,
    })
    throw new Error(errorMessage)
  }

  return {
    acceptCallRequest,
    declineCallRequest,
    getSessionCredentials,
  }
}

export default { createEventSource, create }
export type {
  CallEndedListener,
  CallRequestAcceptedListener,
  CallRequestDeclinedListener,
  CallRequestedListener,
  EventType,
}
